import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { InfoCircleOutlined } from "@ant-design/icons";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { Col, Empty, Form, Input, Row, Tooltip, Typography } from "antd";
import { clsx } from "clsx";
import { $nodesOfType } from "lexical";
import capitalize from "lodash/capitalize";
import uniq from "lodash/uniq";

import { RVForm } from "../../../../atoms/RVForm/RVForm";
import { RVIcon } from "../../../../atoms/RVIcon/RVIcon";
import { RVModal } from "../../../../atoms/RVModal/RVModal";
import { RVButton } from "../../../../molecules/RVButton/RVButton";
import { RVFormFloatingLabelWrap } from "../../../../molecules/RVFormFloatingLabelWrap/RVFormFloatingLabelWrap";
import { CustomVariableNode } from "../../nodes/CustomVariableNode";
import { ConfigurableVariableOption } from "./ConfigurableVariableOption";

import styles from "./VariableMenuTriggerPlugin.module.scss";

/** Result returned by the test-prompt callback. */
export type TestPromptResults = {
    choice: string;
    reason: string;
    score: number;
};

/** A variant choice used for AI prompt testing. */
export type PromptVariant = {
    id: number;
    name: string;
    description: string;
};

type VariableMenuTriggerPlugin = {
    tooltip?: string;
    prompt: string;
    model: string;
    variants?: Array<PromptVariant>;
    customVariables: Array<ConfigurableVariableOption>;
    /** Callback that runs the prompt test against an AI service and returns results. */
    testPrompt: (requestBody: {
        prompt: string;
        choices: Array<{ id: number; name: string; description: string }>;
        model?: string;
    }) => Promise<TestPromptResults>;
};

const TOOLTIP_TEXT = `Add sample values for variables to test your prompt. 
Adjust prompt as needed based on AI response.`;

const NO_CUSTOM_VARIABLES = (
    <div className={styles.emptyContainer}>
        <Empty
            data-testid="not-connected-trigger-event"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="No Trigger Events"
            className={styles.emptyIcon}
        />
        <p className={styles.emptyText}>
            (Optional) Add contact or event variables to your node prompt to test with sample customer data.
        </p>
    </div>
);

export function VariableMenuTriggerPlugin({
    tooltip,
    prompt,
    model,
    variants,
    customVariables,
    testPrompt,
}: VariableMenuTriggerPlugin) {
    const [form] = Form.useForm();
    const [editor] = useLexicalComposerContext();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isPromptRunning, setIsPromptRunning] = useState(false);
    const [selectedCustomVariables, setSelectedCustomVariables] = useState<string[]>([]);
    const [result, setResult] = useState<TestPromptResults>();
    const [responseError, setResponseError] = useState<string>();
    const anyCustomVariable = useMemo(() => selectedCustomVariables.length > 0, [selectedCustomVariables]);
    const ref = useRef<HTMLDivElement>(null);

    const handleCancel = useCallback(() => {
        setIsModalOpen(false);
        setResult(undefined);
        setResponseError(undefined);
    }, []);

    const getCustomVariableType = useCallback(
        (customVariableName: string) =>
            customVariables.find((customVariable) => customVariable.value === customVariableName)?.dataType,
        [customVariables]
    );

    const openPromptTestModal = useCallback(() => {
        const customVariables = editor
            .getEditorState()
            .read(() =>
                $nodesOfType(CustomVariableNode).map((node) =>
                    node.getTextContent().replaceAll("{{", "").replaceAll("}}", "")
                )
            );
        setSelectedCustomVariables(uniq(customVariables));
        setIsModalOpen(true);
    }, [editor]);

    const runTestPrompt = useCallback(
        async (values: Record<string, string>) => {
            const promptWithValues =
                Object.entries(values).reduce(
                    (text, [customVariableName, customVariableValue]) =>
                        text.replaceAll(`{{${customVariableName}}}`, customVariableValue),
                    prompt
                ) ?? prompt;
            setResponseError(undefined);
            setIsPromptRunning(true);

            testPrompt({
                prompt: promptWithValues,
                model,
                choices: (variants ?? []).map((variant, index) => ({ ...variant, id: index + 1 })),
            })
                .then((result) => {
                    setResult(result);
                })
                .catch((err) => {
                    setResponseError(err);
                })
                .finally(() => {
                    setIsPromptRunning(false);
                });
        },
        [prompt, variants, model, testPrompt]
    );

    useEffect(() => {
        ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, [result]);

    const icon = <RVIcon onClick={openPromptTestModal} icon="variable" className={styles.variableIcon} />;

    return (
        <>
            {tooltip ? <Tooltip title={tooltip}>{icon}</Tooltip> : icon}
            {isModalOpen && (
                <RVModal
                    title="Test Your Prompt"
                    className={styles.modal}
                    open={isModalOpen}
                    onCancel={handleCancel}
                    footer={
                        <span className={styles.footerButtonGroup}>
                            {responseError && <span className={styles.apiError}>{responseError}. Please Retry.</span>}
                            <RVButton onClick={form.submit} type="primary" loading={isPromptRunning}>
                                Run Test
                            </RVButton>
                        </span>
                    }
                >
                    <Col className={styles.modalContentContainer}>
                        <RVForm form={form} onFinish={runTestPrompt}>
                            {anyCustomVariable ? (
                                <Col className={clsx(styles.customVariablesContainer, styles.spacedMarginBottom)}>
                                    <Row className={clsx(styles.customVariable, styles.reducedMarginBottom)}>
                                        <Col className={clsx(styles.customVariableName, styles.categoryTitle)}>
                                            <Typography.Text>Variables</Typography.Text>
                                        </Col>
                                        <Col className={clsx(styles.customVariableValue, styles.categoryTitle)}>
                                            <Typography.Text>Test Value</Typography.Text>
                                            <Tooltip title={TOOLTIP_TEXT}>
                                                <InfoCircleOutlined className={styles.info} />
                                            </Tooltip>
                                        </Col>
                                    </Row>

                                    {selectedCustomVariables.map((customVariableName) => (
                                        <Row
                                            key={`custom-variable-${customVariableName}`}
                                            className={styles.customVariable}
                                        >
                                            <Col className={styles.customVariableName}>{customVariableName}</Col>

                                            <Col className={styles.customVariableValue}>
                                                <RVFormFloatingLabelWrap name={customVariableName}>
                                                    <Input
                                                        className={styles.customVariableValueInput}
                                                        placeholder={capitalize(
                                                            getCustomVariableType(customVariableName) ??
                                                                "Add Test Value"
                                                        )}
                                                    />
                                                </RVFormFloatingLabelWrap>
                                            </Col>
                                        </Row>
                                    ))}
                                </Col>
                            ) : (
                                NO_CUSTOM_VARIABLES
                            )}
                        </RVForm>

                        <Row className={styles.categoryTitle}>Test Results</Row>
                        <div className={styles.result} ref={ref}>
                            <Typography.Text className={styles.bold}>Result: {result?.choice || "-"}</Typography.Text>
                            <Typography.Text className={styles.italic}>
                                AI Reason: {result?.reason || "-"}
                            </Typography.Text>
                            <Typography.Text className={styles.italic}>Score: {result?.score || "-"}</Typography.Text>
                        </div>
                    </Col>
                </RVModal>
            )}
        </>
    );
}
